/**
 * Talent Cloud — My Roster
 * Internal CRM table view of assigned exclusive creators.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sidebar } from '../../components/Sidebar';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Instagram, Youtube, IndianRupee, ChevronRight, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n);

const AVAILABILITY = {
  available: { label: 'Available', color: '#00C972', bg: 'rgba(0,201,114,0.15)' },
  booked: { label: 'Booked', color: '#FF451A', bg: 'rgba(255,69,26,0.15)' },
  vacation: { label: 'Vacation', color: '#FFB800', bg: 'rgba(255,184,0,0.15)' },
  paused: { label: 'Paused', color: '#999999', bg: 'rgba(255,255,255,0.05)' },
  locked: { label: 'Exclusive Lock', color: '#9B6EFF', bg: 'rgba(155,110,255,0.15)' },
};

export const TalentRoster = () => {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = {};
        if (q) params.q = q;
        if (availabilityFilter !== 'all') params.availability = availabilityFilter;
        const { data } = await axios.get(`${API_URL}/api/talent/roster`, { params, withCredentials: true });
        setCreators(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, availabilityFilter]);

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="talent-roster-page">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF451A] mb-2">Talent Cloud</div>
            <h1 className="font-display text-5xl tracking-tight text-[#0F0F0F] dark:text-white mb-2">My Roster.</h1>
            <p className="text-[#6B6B6B] dark:text-zinc-400">Your assigned creators. Click any row to open their workspace.</p>
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2 relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B] dark:text-zinc-500" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name, email, handle…"
                  className="pl-9 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                  data-testid="roster-search-input"
                />
              </div>
              <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
                <SelectTrigger className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="roster-availability-filter">
                  <Filter size={12} className="mr-1 text-[#6B6B6B] dark:text-zinc-500" />
                  <SelectValue placeholder="All availability" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Availability</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="vacation">Vacation</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="locked">Exclusive Lock</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-12"><div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div></div>
          ) : creators.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center text-[#6B6B6B] dark:text-zinc-500">No creators in your roster yet.</div>
          ) : (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-hidden">
              <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_1.5fr_120px] gap-3 px-5 py-3 border-b border-black/[0.08] dark:border-white/5 bg-black/40 font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">
                <div>Creator</div>
                <div>Reach</div>
                <div>ER</div>
                <div>Pricing (Reel)</div>
                <div>Categories</div>
                <div className="text-right">Status</div>
              </div>
              {creators.map((c, i) => {
                const meta = AVAILABILITY[c.availability_status] || AVAILABILITY.available;
                return (
                  <motion.div key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.4) }}>
                    <Link
                      to={`/talent/creators/${c.id}`}
                      className="grid grid-cols-2 md:grid-cols-[2fr_1fr_1fr_1fr_1.5fr_120px] gap-3 px-5 py-4 border-b border-black/[0.08] dark:border-white/5 last:border-b-0 hover:bg-white/[0.03] group transition-colors items-center"
                      data-testid="roster-row"
                    >
                      <div className="col-span-2 md:col-span-1 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF451A] to-[#9B6EFF] flex items-center justify-center text-[#0F0F0F] dark:text-white font-bold shrink-0">{c.name?.[0]?.toUpperCase()}</div>
                        <div className="min-w-0">
                          <div className="text-[#0F0F0F] dark:text-white font-medium group-hover:text-[#FF451A] transition-colors truncate">{c.name}</div>
                          <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 truncate">@{c.instagram_handle || c.email}</div>
                        </div>
                      </div>
                      <div className="text-sm">
                        <div className="text-[#0F0F0F] dark:text-white font-mono flex items-center gap-1"><Instagram size={11} className="text-[#6B6B6B] dark:text-zinc-500" /> {fmtNum(c.instagram_followers || 0)}</div>
                        {c.youtube_subscribers > 0 && <div className="text-[#6B6B6B] dark:text-zinc-500 text-xs font-mono flex items-center gap-1"><Youtube size={11} /> {fmtNum(c.youtube_subscribers)}</div>}
                      </div>
                      <div className="text-sm text-[#0F0F0F] dark:text-white font-mono">{(c.instagram_engagement || 0).toFixed(1)}%</div>
                      <div className="text-sm text-[#0F0F0F] dark:text-white font-mono flex items-center gap-1"><IndianRupee size={11} className="text-[#6B6B6B] dark:text-zinc-500" />{(c.pricing_reel || 0).toLocaleString('en-IN')}</div>
                      <div className="hidden md:flex flex-wrap gap-1">
                        {(c.categories || []).slice(0, 2).map((cat) => (
                          <span key={cat} className="text-[10px] bg-white/5 border border-black/[0.10] dark:border-white/10 text-zinc-300 px-2 py-0.5 rounded-full">{cat}</span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 justify-end">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider" style={{ backgroundColor: meta.bg, color: meta.color }}>{meta.label}</span>
                        <ChevronRight size={14} className="text-[#6B6B6B] dark:text-zinc-500 group-hover:text-[#FF451A] transition-colors" />
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
